import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';

import { ClockControlService } from './clock-control.service';
import { ClockDevice } from '../models/clock-control.model';

@Component({
    selector: 'app-clock-control',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './clock-control.component.html',
    styleUrls: ['./clock-control.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ClockControlComponent implements OnInit, OnDestroy {
    clockDevices: ClockDevice[] = [];
    pagedClockDevices: ClockDevice[] = [];

    isLoading = false;
    isSaving = false;
    errorMessage: string | null = null;

    currentPage = 1;
    totalPages = 1;
    pageSize = 25;
    pageSizes = [10, 25, 50, 100];

    modalOpen = false;
    currentClock: ClockDevice | null = null;
    formModel = this.getEmptyClockForm();

    toggleConfirmOpen = false;
    clockToToggle: ClockDevice | null = null;

    toastVisible = false;
    toastMessage = '';
    toastType: 'success' | 'error' = 'success';

    constructor(
        private clockControlService: ClockControlService,
        private cd: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.loadClockDevices();
    }

    ngOnDestroy(): void {
        this.unlockBodyScroll();
    }

    private lockBodyScroll(): void {
        document.body.style.overflow = 'hidden';
    }

    private unlockBodyScroll(): void {
        document.body.style.overflow = '';
    }

    private syncBodyScrollLock(): void {
        if (this.modalOpen || this.toggleConfirmOpen) {
            this.lockBodyScroll();
            return;
        }

        this.unlockBodyScroll();
    }

    loadClockDevices(): void {
        this.isLoading = true;
        this.errorMessage = null;

        this.clockControlService.getClockDevices().subscribe({
            next: (devices) => {
                this.clockDevices = devices ?? [];
                this.sortClockDevices();
                this.rebuildPagedClockDevices();
                this.isLoading = false;
                this.cd.detectChanges();
            },
            error: (err) => {
                console.error('Failed to load clock devices:', err);
                this.errorMessage = 'Failed to load clock devices';
                this.clockDevices = [];
                this.pagedClockDevices = [];
                this.isLoading = false;
                this.cd.detectChanges();
            },
        });
    }

    private sortClockDevices(): void {
        this.clockDevices = [...this.clockDevices].sort((a, b) => {
            if (a.isActive && !b.isActive) return -1;
            if (!a.isActive && b.isActive) return 1;
            return a.id - b.id;
        });
    }

    private rebuildPagedClockDevices(): void {
        this.totalPages = Math.max(1, Math.ceil(this.clockDevices.length / this.pageSize));

        if (this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages;
        }

        const start = (this.currentPage - 1) * this.pageSize;
        const end = start + this.pageSize;

        this.pagedClockDevices = this.clockDevices.slice(start, end);
    }

    changePage(page: number): void {
        if (page < 1 || page > this.totalPages) return;
        this.currentPage = page;
        this.rebuildPagedClockDevices();
    }

    changePageSize(size: number): void {
        this.pageSize = Number(size) || 25;
        this.currentPage = 1;
        this.rebuildPagedClockDevices();
    }

    openEditModal(device: ClockDevice): void {
        this.currentClock = device;
        this.formModel = {
            name: device.name ?? '',
        };

        this.modalOpen = true;
        this.syncBodyScrollLock();
    }

    closeModal(force = false): void {
        if (this.isSaving && !force) return;

        this.modalOpen = false;
        this.currentClock = null;
        this.formModel = this.getEmptyClockForm();

        this.syncBodyScrollLock();
    }

    submitClock(form?: NgForm): void {
        if (!this.currentClock) return;

        const name = String(this.formModel.name ?? '').trim();

        if (!name) {
            this.showToast('Clock name is required', 'error');
            return;
        }

        if (name === (this.currentClock.name ?? '').trim()) {
            this.closeModal();
            return;
        }

        this.isSaving = true;
        this.cd.detectChanges();

        this.clockControlService.updateClockDeviceName(this.currentClock.id, { name }).subscribe({
            next: (ok) => {
                if (!ok) {
                    this.showToast('Failed to update clock name', 'error');
                    this.isSaving = false;
                    this.cd.detectChanges();
                    return;
                }

                this.isSaving = false;
                this.closeModal(true);
                this.showToast('Clock name updated successfully', 'success');
                this.loadClockDevices();
            },
            error: (err) => {
                console.error(err);
                this.showToast('Failed to update clock name', 'error');
                this.isSaving = false;
                this.cd.detectChanges();
            },
        });
    }

    confirmToggle(device: ClockDevice): void {
        this.clockToToggle = device;

        this.toggleConfirmOpen = true;
        this.syncBodyScrollLock();
    }

    cancelToggle(force = false): void {
        if (this.isSaving && !force) return;

        this.toggleConfirmOpen = false;
        this.clockToToggle = null;

        this.syncBodyScrollLock();
    }

    toggleClock(): void {
        if (!this.clockToToggle) return;

        const nextIsActive = !this.clockToToggle.isActive;

        this.isSaving = true;
        this.cd.detectChanges();

        this.clockControlService
            .toggleClockDeviceActive(this.clockToToggle.id, { isActive: nextIsActive })
            .subscribe({
                next: (ok) => {
                    if (!ok) {
                        this.showToast(
                            nextIsActive
                                ? 'Failed to activate clock device'
                                : 'Failed to deactivate clock device',
                            'error',
                        );
                        this.isSaving = false;
                        this.cd.detectChanges();
                        return;
                    }

                    this.isSaving = false;
                    this.cancelToggle(true);
                    this.showToast(
                        nextIsActive
                            ? 'Clock device activated successfully'
                            : 'Clock device deactivated successfully',
                        'success',
                    );
                    this.loadClockDevices();
                },
                error: (err) => {
                    console.error(err);
                    this.showToast(
                        nextIsActive
                            ? 'Failed to activate clock device'
                            : 'Failed to deactivate clock device',
                        'error',
                    );
                    this.isSaving = false;
                    this.cd.detectChanges();
                },
            });
    }

    getStatusLabel(device: ClockDevice): string {
        return device.isActive ? 'Active' : 'Inactive';
    }

    formatLastSync(value: string | null | undefined): string {
        if (!value) return 'Never';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return date.toLocaleString();
    }

    getIpAddressLabel(device: ClockDevice): string {
        return device.ipAddress?.trim() || '—';
    }

    getCoordinatesLabel(device: ClockDevice): string {
        const lat = Number(device.latitude ?? 0);
        const lng = Number(device.longitude ?? 0);

        if (lat === 0 && lng === 0) return '—';

        return `${lat}, ${lng}`;
    }

    trackByClockId(_: number, item: ClockDevice): number {
        return item.id;
    }

    showToast(message: string, type: 'success' | 'error'): void {
        this.toastMessage = message;
        this.toastType = type;
        this.toastVisible = true;

        setTimeout(() => {
            this.toastVisible = false;
            this.cd.markForCheck();
        }, 2500);
    }

    private getEmptyClockForm(): { name: string } {
        return {
            name: '',
        };
    }
}